import React, {useEffect, useMemo, useState} from "react";
import {useDispatch, useSelector} from "react-redux";
import {Button} from "primereact/button";
import {ConfirmDialog, confirmDialog} from "primereact/confirmdialog";
import {DataView} from "primereact/dataview";
import {Dialog} from "primereact/dialog";
import {IconField} from "primereact/iconfield";
import {InputIcon} from "primereact/inputicon";
import {InputText} from "primereact/inputtext";
import {Message} from "primereact/message";
import {ProgressSpinner} from "primereact/progressspinner";
import Avatar from "../../../system/components/avatar";
import Icon from "../../../system/components/icon";
import Span from "../../../system/components/span";
import {setMessage} from "../../../system/app-slice";
import {useTranslate} from "../../../system/useTranslate";
import {useTeamNote} from "../../../models/team-note/use-team-note";
import PageHeaderButton from "../../components/page-header-button";
import {RichTextEditor, RichTextView, richTextToPlain} from "../../../system/components/rich-text";

const EMPTY_FORM = {title: "", content: ""};

function Note({teamData}) {
    const translate = useTranslate();
    const dispatch = useDispatch();
    const membership = useSelector((state) => state.app.workspaceMembershipData);
    const noteApi = useTeamNote();
    const [notes, setNotes] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [search, setSearch] = useState("");
    const [dialogVisible, setDialogVisible] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form, setForm] = useState(EMPTY_FORM);
    const [saving, setSaving] = useState(false);
    const [formError, setFormError] = useState(null);

    const load = async () => {
        setLoading(true);
        setError(null);
        try { setNotes(await noteApi.getTeamNotes(teamData.documentId)); }
        catch (requestError) { setError(requestError?.message || "team-notes.error.load"); }
        finally { setLoading(false); }
    };

    useEffect(() => {
        load();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [teamData?.documentId]);

    const filtered = useMemo(() => {
        const query = search.trim().toLocaleLowerCase();
        if (!query) return notes;
        return notes.filter((note) => [note.title, richTextToPlain(note.content), note?.author?.member?.fullname]
            .some((value) => String(value || "").toLocaleLowerCase().includes(query)));
    }, [notes, search]);

    const openCreate = () => {
        setEditing(null);
        setForm(EMPTY_FORM);
        setFormError(null);
        setDialogVisible(true);
    };
    const openEdit = (note) => {
        setEditing(note);
        setForm({title: note.title || "", content: note.content || ""});
        setFormError(null);
        setDialogVisible(true);
    };

    const save = async () => {
        if (!form.title.trim() || !form.content.trim()) {
            setFormError("team-notes.error.required");
            return;
        }
        setSaving(true);
        setFormError(null);
        try {
            if (editing) await noteApi.updateTeamNote(editing.documentId || editing.id, form);
            else await noteApi.createTeamNote({...form, team: teamData.documentId});
            dispatch(setMessage({severity: "success", content: translate(editing ? "team-notes.updated" : "team-notes.created")}));
            setDialogVisible(false);
            await load();
        } catch (requestError) {
            setFormError(requestError?.message || "team-notes.error.save");
        } finally {
            setSaving(false);
        }
    };

    const archive = (note) => confirmDialog({
        header: translate("team-notes.archive-title"),
        message: translate("team-notes.archive-message").replace("{{TITLE}}", note.title),
        icon: "pi pi-exclamation-triangle",
        acceptLabel: translate("archive"),
        rejectLabel: translate("cancel"),
        acceptClassName: "p-button-danger text-xs",
        rejectClassName: "p-button-text text-xs",
        accept: async () => {
            try {
                await noteApi.archiveTeamNote(note.documentId || note.id);
                dispatch(setMessage({severity: "success", content: translate("team-notes.archived")}));
                await load();
            } catch (requestError) {
                dispatch(setMessage({severity: "error", content: translate(requestError?.message || "team-notes.error.archive")}));
            }
        },
    });

    const canEdit = (note) => String(note?.author?.documentId || note?.author?.id) === String(membership?.documentId || membership?.id);
    const itemTemplate = (note) => <div className="p-2 w-full">
        <article className="surface-card border-1 border-100 border-round-xl shadow-1 p-4 flex flex-column gap-3">
            <header className="flex align-items-start justify-content-between gap-3">
                <div className="flex align-items-center gap-2 min-w-0"><Avatar memberData={note?.author?.member}/><div className="min-w-0">
                    <strong className="block text-sm text-900 text-overflow-ellipsis overflow-hidden">{note.title}</strong>
                    <small className="text-xs text-500">{note?.author?.member?.fullname || translate("team.member.unknown")}</small>
                </div></div>
                <div className="flex gap-1">
                    {canEdit(note) && <Button text rounded icon="pi pi-pencil" aria-label={translate("edit")} onClick={() => openEdit(note)}/>}
                    {canEdit(note) && <Button text rounded severity="danger" icon="pi pi-box" aria-label={translate("archive")} onClick={() => archive(note)}/>}
                </div>
            </header>
            <RichTextView className="m-0 text-xs text-700 line-height-3" value={note.content}/>
            <footer className="border-top-1 border-100 pt-3 text-xs text-400 flex align-items-center gap-2"><Icon icon="mi mi:schedule"/>
                <Span type="PERSIAN_DATE" format="YYYY/MM/DD - HH:mm">{note.updatedAt || note.createdAt}</Span>
            </footer>
        </article>
    </div>;

    return <div className="h-full overflow-auto surface-50 p-3 flex flex-column gap-4">
        <ConfirmDialog className="rtl"/>
        <PageHeaderButton title={translate("team-notes.title")} description={translate("team-notes.description")} end={<div className="flex flex-column md:flex-row gap-2">
            <IconField dir="rtl"><InputIcon className="pi pi-search"/><InputText value={search} onChange={(event) => setSearch(event.target.value)} placeholder={translate("team-notes.search")}/></IconField>
            <Button className="text-xs gap-2" icon="pi pi-plus" label={translate("team-notes.create")} onClick={openCreate}/>
        </div>}/>
        {loading ? <div className="flex-1 flex align-items-center justify-content-center"><ProgressSpinner/></div>
            : error ? <div className="flex flex-column align-items-center gap-3"><Message severity="error" text={translate(error)}/><Button outlined className="text-xs gap-2" icon="pi pi-refresh" label={translate("team.retry")} onClick={load}/></div>
            : <DataView value={filtered} paginator rows={8} layout="list" itemTemplate={itemTemplate}
                emptyMessage={translate(search ? "team-notes.search-empty" : "team-notes.empty")}/>}

        <Dialog className="rtl w-11 md:w-7 lg:w-5" visible={dialogVisible} modal header={translate(editing ? "team-notes.edit" : "team-notes.create")}
            onHide={() => !saving && setDialogVisible(false)} footer={<div className="flex flex-row-reverse gap-2">
                <Button className="text-xs gap-2" icon="pi pi-save" loading={saving} label={translate("save")} onClick={save}/>
                <Button outlined className="text-xs" disabled={saving} label={translate("cancel")} onClick={() => setDialogVisible(false)}/>
            </div>}>
            <div className="flex flex-column gap-3 pt-2">
                <label htmlFor="team-note-title" className="text-xs font-medium">{translate("team-notes.field.title")}</label>
                <InputText id="team-note-title" value={form.title} maxLength={160} onChange={(event) => setForm((current) => ({...current, title: event.target.value}))}/>
                <label htmlFor="team-note-content" className="text-xs font-medium">{translate("team-notes.field.content")}</label>
                <RichTextEditor ariaLabel={translate("team-notes.field.content")} value={form.content} onChange={(html) => setForm((current) => ({...current, content: html}))} minHeight="10rem"/>
                {formError && <Message severity="error" text={translate(formError)}/>}
            </div>
        </Dialog>
    </div>;
}

export default Note;